import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { Header as DatagridHeader } from "@dashboard/components/Datagrid/components/Header";
import { readonlyTextCell, textCell } from "@dashboard/components/Datagrid/customCells/cells";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeOpts,
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { useDatagridTheme } from "@dashboard/components/Datagrid/styles";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { WsmErrorFragment } from "@dashboard/graphql";
import { GridCell, Item } from "@glideapps/glide-data-grid";
import { makeStyles } from "@saleor/macaw-ui";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getWsmComposeErrorMessage } from "../../errors";
import { valueMessages } from "../../messages";
import { WsmOptionValueDraft } from "../../types";
import { getTierDelta } from "../../utils";
import {
  columnTextWidth,
  ellipsize,
  gridTextMeasurer,
  TIER_COLUMN_PREFIX,
  tierColumnGroupId,
  tierColumnId,
  tierColumnLabel,
  VALUE_COLUMN_WIDTHS,
  WsmTierGroup,
} from "./columns";

/** The choice column is what every other column is read against, so it never hides. */
const PINNED_COLUMN = "name";

const useStyles = makeStyles(
  {
    // glide-data-grid scrolls its canvas horizontally, and Chromium's overlay
    // scrollbar never paints on it: at 1280 a merchant saw four of nine dealer
    // groups with nothing on screen saying the grid continued. This makes the
    // bar permanent, which is the whole affordance.
    scrollable: {
      // Styling ::-webkit-scrollbar is what opts Chromium out of an overlay
      // scrollbar, which is why scrollbar-width is deliberately NOT set here:
      // the standard property wins and takes the permanent bar away again.
      "& .dvn-scroller": {
        overflowX: "auto",
      },
      "& .dvn-scroller::-webkit-scrollbar": {
        height: 10,
      },
      "& .dvn-scroller::-webkit-scrollbar-track": {
        backgroundColor: "rgba(0, 0, 0, 0.06)",
      },
      "& .dvn-scroller::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.32)",
        borderRadius: 5,
      },
    },
  },
  { name: "WsmOptionValuesCard" },
);

export interface WsmOptionValuesCardProps {
  values: WsmOptionValueDraft[];
  /** One error per value index, as the payload named them. */
  rowErrors: Record<number, WsmErrorFragment>;
  /** Dealer groups, in the order the dealer section lists them. */
  tierGroups: WsmTierGroup[];
  disabled: boolean;
  unsavedCount: number;
  onAdd: () => void;
  onRemove: (keys: string[]) => void;
  onValueChange: (key: string, patch: Partial<WsmOptionValueDraft>) => void;
  onTierDeltaChange: (key: string, dealerGroup: string, raw: string) => void;
}

/**
 * The grid a question's answers are actually edited in, retail delta and every
 * dealer group's delta side by side, which is the one view the Django admin
 * never gave a merchant: it split them across an inline and a nested inline.
 * Datagrid brings its own DashboardCard, so this must NOT be wrapped in a
 * second one or the table paints outside it.
 */
export const WsmOptionValuesCard = ({
  values,
  rowErrors,
  tierGroups,
  disabled,
  unsavedCount,
  onAdd,
  onRemove,
  onValueChange,
  onTierDeltaChange,
}: WsmOptionValuesCardProps) => {
  const intl = useIntl();
  const classes = useStyles();
  const datagridState = useDatagridChangeState();
  const datagridTheme = useDatagridTheme(disabled);
  // Measured against the font the grid itself draws with, so what is marked as
  // cut is what would really have been cut.
  const clipName = useMemo(() => {
    const measure = gridTextMeasurer(
      [datagridTheme.baseFontStyle, datagridTheme.fontFamily].join(" "),
    );
    const width = columnTextWidth(VALUE_COLUMN_WIDTHS.name);

    return (name: string) => (measure ? ellipsize(name, width, measure) : name);
  }, [datagridTheme]);
  const allColumns: AvailableColumn[] = useMemo(
    () => [
      {
        id: PINNED_COLUMN,
        title: intl.formatMessage(valueMessages.colName),
        width: VALUE_COLUMN_WIDTHS.name,
      },
      {
        id: "skuFragment",
        title: intl.formatMessage(valueMessages.colSkuFragment),
        width: VALUE_COLUMN_WIDTHS.skuFragment,
      },
      {
        // No currency in the heading: a question is shared across products, and
        // two of them can price in two currencies.
        id: "priceDelta",
        title: intl.formatMessage(valueMessages.colPriceDeltaNoCurrency),
        width: VALUE_COLUMN_WIDTHS.priceDelta,
      },
      // The dealer columns sit next to the retail delta, not after the image,
      // because comparing the two is the whole reason this screen exists. The
      // image URL is the one that may scroll off.
      ...tierGroups.map(group => {
        const label = tierColumnLabel(group);

        return {
          id: tierColumnId(group),
          // The NAME heads the column. The code is the secondary label: it
          // still names the column in the picker and on hover, because prices
          // and the storefront key on it, but it is no longer what a merchant
          // reads a price against.
          title: label.title,
          pickerTitle: label.code ? `${label.title} (${label.code})` : label.title,
          width: VALUE_COLUMN_WIDTHS.tierDelta,
        };
      }),
      {
        id: "imageUrl",
        title: intl.formatMessage(valueMessages.colImageUrl),
        width: VALUE_COLUMN_WIDTHS.imageUrl,
      },
    ],
    [intl, tierGroups],
  );
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const columns = useMemo(
    () => allColumns.filter(column => !hiddenColumns.includes(column.id)),
    [allColumns, hiddenColumns],
  );
  const toggleColumn = useCallback((columnId: string) => {
    // Hiding the choice column would leave a grid of numbers with nothing to
    // read them against, so that one is not offered as a toggle.
    if (columnId === PINNED_COLUMN) {
      return;
    }

    setHiddenColumns(current =>
      current.includes(columnId)
        ? current.filter(id => id !== columnId)
        : [...current, columnId],
    );
  }, []);
  const getCellContent = useCallback(
    ([column, row]: Item): GridCell => {
      const value = values[row];
      const columnId = columns[column]?.id;

      if (!value || !columnId) {
        return readonlyTextCell("");
      }

      if (columnId.startsWith(TIER_COLUMN_PREFIX)) {
        // Empty is a real state, not a missing one: it means this group pays the
        // retail delta. A zero here would mean it pays nothing extra.
        return textCell(getTierDelta(value, tierColumnGroupId(columnId)), {
          contentAlign: "right",
        });
      }

      switch (columnId) {
        case PINNED_COLUMN:
          // The cell still CARRIES the whole name: it is what the editor opens
          // with, what a copy takes, and what the tooltip shows.
          return textCell(value.name, { displayData: clipName(value.name) });
        case "skuFragment":
          return textCell(value.skuFragment);
        case "priceDelta":
          // A text cell, not a money cell: the delta is SIGNED and the
          // merchant's keystrokes go to the server verbatim, so the floor
          // refusals are something they can see rather than a rounding they
          // cannot.
          return textCell(value.priceDelta, { contentAlign: "right" });
        case "imageUrl":
          return textCell(value.imageUrl);
        default:
          return readonlyTextCell("");
      }
    },
    [values, columns, clipName],
  );
  const getCellTooltipContent = useCallback(
    ([column, row]: Item) => {
      const value = values[row];

      if (!value || columns[column]?.id !== PINNED_COLUMN || clipName(value.name) === value.name) {
        return undefined;
      }

      return value.name;
    },
    [values, columns, clipName],
  );
  /** Hovering a dealer heading hands back the group code the column stores. */
  const getColumnTooltipContent = useCallback(
    (columnIndex: number) => {
      const columnId = columns[columnIndex]?.id ?? "";

      if (!columnId.startsWith(TIER_COLUMN_PREFIX)) {
        return "";
      }

      const group = tierGroups.find(
        candidate => candidate.id === tierColumnGroupId(columnId),
      );

      return group ? tierColumnLabel(group).code : "";
    },
    [columns, tierGroups],
  );
  const handleChange = useCallback(
    (opts: DatagridChangeOpts) => {
      const update = opts.currentUpdate;
      const value = update ? values[update.row] : undefined;

      if (update && value) {
        const next = String(update.data ?? "");

        if (update.column.startsWith(TIER_COLUMN_PREFIX)) {
          onTierDeltaChange(value.key, tierColumnGroupId(update.column), next);
        } else if (update.column === "priceDelta") {
          onValueChange(value.key, { priceDelta: next.trim() });
        } else if (update.column === PINNED_COLUMN) {
          onValueChange(value.key, { name: next });
        } else if (update.column === "skuFragment") {
          onValueChange(value.key, { skuFragment: next.trim() });
        } else if (update.column === "imageUrl") {
          onValueChange(value.key, { imageUrl: next.trim() });
        }
      }

      // The form state is the single source of truth for the grid, so the
      // grid's own change buffer is emptied rather than reconciled at save.
      datagridState.clear();
    },
    [values, onValueChange, onTierDeltaChange, datagridState],
  );
  const firstError = Object.keys(rowErrors).length
    ? rowErrors[Number(Object.keys(rowErrors)[0])]
    : undefined;
  const errorText = getWsmComposeErrorMessage(firstError, intl);

  return (
    <Box data-test-id="wsm-option-values" className={classes.scrollable}>
      <DatagridChangeStateContext.Provider value={datagridState}>
        <Datagrid
          availableColumns={columns}
          rows={values.length}
          emptyText={intl.formatMessage(valueMessages.empty)}
          getCellContent={getCellContent}
          getCellError={([, row]: Item) => !!rowErrors[row]}
          getCellTooltipContent={getCellTooltipContent}
          getColumnTooltipContent={getColumnTooltipContent}
          // The choice stays put while the dealer columns scroll under it,
          // which is the only way a price has anything to name it at 1024.
          freezeColumns={1}
          renderColumnPicker={() => (
            <ColumnPicker
              staticColumns={allColumns}
              selectedColumns={columns.map(column => column.id)}
              onToggle={toggleColumn}
            />
          )}
          menuItems={index => [
            {
              label: intl.formatMessage(valueMessages.removeRow),
              onSelect: () => onRemove([values[index]?.key]),
            },
          ]}
          selectionActions={(selection, { removeRows }) => (
            <Button
              variant="tertiary"
              disabled={disabled}
              onClick={() => {
                onRemove(selection.map(index => values[index]?.key).filter(Boolean));
                removeRows(selection);
              }}
              data-test-id="wsm-option-value-remove-selected"
            >
              {intl.formatMessage(valueMessages.removeSelected)}
            </Button>
          )}
          onChange={handleChange}
          readonly={disabled}
          rowMarkers="checkbox"
          verticalBorder={false}
          renderHeader={({ isFullscreenOpen, toggleFullscreen }) => (
            <DatagridHeader
              title={intl.formatMessage(valueMessages.title, { count: values.length })}
            >
              {unsavedCount > 0 && (
                <Text size={2} color="default2" data-test-id="wsm-option-value-unsaved">
                  {intl.formatMessage(valueMessages.unsaved, { count: unsavedCount })}
                </Text>
              )}
              <DatagridHeader.ButtonFullScreen
                isOpen={isFullscreenOpen}
                onToggle={toggleFullscreen}
              >
                {intl.formatMessage(valueMessages.fullScreen)}
              </DatagridHeader.ButtonFullScreen>
              <Button
                variant="secondary"
                disabled={disabled}
                onClick={onAdd}
                data-test-id="wsm-option-value-add"
              >
                {intl.formatMessage(valueMessages.addValue)}
              </Button>
            </DatagridHeader>
          )}
        />
      </DatagridChangeStateContext.Provider>
      <Box paddingX={6} paddingTop={2} display="grid" gap={1}>
        {errorText && (
          <Text size={2} color="critical1" data-test-id="wsm-option-value-error">
            {errorText}
          </Text>
        )}
        <Text size={2} color="default2" data-test-id="wsm-option-value-replace">
          {intl.formatMessage(valueMessages.saveReplaces)}
        </Text>
        <Text size={2} color="default2">
          {intl.formatMessage(
            tierGroups.length ? valueMessages.signedHelp : valueMessages.noDealerGroups,
          )}
        </Text>
        {tierGroups.length > 0 && (
          <Text size={2} color="default2" data-test-id="wsm-option-value-columns">
            {intl.formatMessage(valueMessages.dealerColumnsHelp, { count: tierGroups.length })}
            {hiddenColumns.length > 0 &&
              ` ${intl.formatMessage(valueMessages.dealerColumnsHidden, {
                count: hiddenColumns.length,
              })}`}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default WsmOptionValuesCard;
